/**
 * The local-first record model.
 *
 * Every record the learner owns carries the same four sync fields from the day it
 * is written, even though nothing syncs yet: a stable UUID minted on this device,
 * a `schemaVersion` so an older payload can be migrated rather than discarded, a
 * monotonic `revision` for conflict detection, and `updatedAt`. Deletions leave a
 * `deletedAt` tombstone rather than removing the row, so a delete can eventually
 * reach another iPad.
 *
 * Adding these now costs nothing and means the sync phase is a new module rather
 * than a migration of every record the learner already has.
 */

import type { Stroke } from '../ink/types';
import type { OverrideMap } from '../ink/overrides';
import type { Clef } from '../theory/staff';

/** Bump when a payload shape changes, and add a case to the matching migrate(). */
export const SCHEMA_VERSION = 1;

export interface SyncMeta {
  id: string;
  schemaVersion: number;
  /** Increments on every write. Never decreases, never reused. */
  revision: number;
  /** ms since epoch. */
  updatedAt: number;
  createdAt: number;
  /** ms since epoch when removed, or null while live. */
  deletedAt: number | null;
}

/**
 * A stroke in staff-relative units rather than canvas pixels.
 *
 * `x` is staff-spaces right of the writing origin, `y` is staff-spaces above the
 * bottom staff line. Both survive a resize, a rotation and a different device,
 * because the staff is the only thing they are measured against.
 */
export interface RelativeStroke {
  id: string;
  color: string;
  /** Width in staff-spaces, so ink keeps its weight relative to the staff. */
  width: number;
  tool: Stroke['tool'];
  fromPen: boolean;
  points: { x: number; y: number; pressure: number; tiltX: number; tiltY: number; t: number }[];
}

export interface DocumentPayload {
  clef: Clef;
  keyId: string;
  /** Whether the key is being detected from the writing. */
  autoKey: boolean;
  strokes: RelativeStroke[];
  /** Manual accidental corrections, keyed by the strokes that produced the note. */
  overrides: OverrideMap;
}

export interface StoredDocument extends SyncMeta {
  title: string;
  /** Which part of the app produced it: 'build', 'fib', 'learn'. */
  feature: string;
  payload: DocumentPayload;
}

/** Small resumable workflow state. Never transient UI — no popovers, no toasts. */
export interface FeatureStateRecord extends SyncMeta {
  /** 'app' for the active destination, otherwise the feature name. */
  key: string;
  value: Record<string, unknown>;
}

export interface SettingsRecord extends SyncMeta {
  key: string;
  value: Record<string, unknown>;
}

/** Append-only. A recorded attempt is never edited. */
export interface PracticeSessionRecord extends SyncMeta {
  feature: string;
  lessonId: string | null;
  result: Record<string, unknown>;
  startedAt: number;
  completedAt: number | null;
}

// ------------------------------------------------------------------- helpers

/**
 * A UUID. `crypto.randomUUID` where it exists, and a v4-shaped fallback built
 * from `getRandomValues` for older Safari, which is exactly the browser an iPad
 * might be running.
 */
export function newId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Everything a record needs before it can be synced.
 *
 * Kept as an assertion about the record model rather than as part of any
 * transport: these fields have been written since before sync existed, and this
 * is what proves a record minted today could still be pushed tomorrow.
 */
export function isSyncable(record: Partial<SyncMeta>): boolean {
  return (
    typeof record.id === 'string' &&
    record.id.length > 0 &&
    typeof record.schemaVersion === 'number' &&
    typeof record.revision === 'number' &&
    record.revision >= 1 &&
    typeof record.updatedAt === 'number' &&
    (record.deletedAt === null || typeof record.deletedAt === 'number')
  );
}

export function freshMeta(now = Date.now()): SyncMeta {
  return {
    id: newId(),
    schemaVersion: SCHEMA_VERSION,
    revision: 1,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
  };
}

/** Advance a record for a new write. Revision is monotonic; callers never set it. */
export function bumped<T extends SyncMeta>(record: T, now = Date.now()): T {
  return { ...record, revision: record.revision + 1, updatedAt: now };
}
